// Removing a stored file is a CHOICE, not a confirmation.
//
// Deleting an expense line is cheap — it is retyped in half a minute. Deleting a
// signed agreement may destroy the only copy: the R2 object goes with the row and
// there is no undo. But "never delete" is wrong too, because a test file uploaded by
// mistake would then be permanent. So one dialog offers both, each labelled with what
// it actually does, and the file is NAMED at the top so neither option can be taken
// against the wrong row.
//
// The archive option leads and is styled as the ordinary action; permanent delete sits
// below it, marked danger, with the consequence spelled out rather than implied.
//
// SHARED by the two things in this app that are stored documents rather than data: an
// office business document, and a retainer's signed agreement. Both offer the identical
// choice for the identical reason, and the labels are the only difference — so they are
// props, and the behaviour (which button is safe, which is red, what the busy state
// does, where the error lands) has one definition. `labels` comes straight from an i18n
// section: he.documents and he.agreements both carry the same five keys.
import { useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import { ErrorText } from './Loader';

export default function FileRemoveModal({
  labels, name, subtitle, fileName, onClose, onArchive, onDelete,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const run = (action) => async () => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Modal title={labels.removeTitle} onClose={onClose}>
      <p>{labels.removeQuestion}</p>

      {/* The file, named: its title, what it belongs to, and the filename itself —
          the same facts the row shows, so there is no doubt which one is about to
          be removed. */}
      <div className="file-remove-subject">
        <strong>{name}</strong>
        {subtitle && <div className="muted">{subtitle}</div>}
        <div className="muted">{fileName}</div>
      </div>

      <div className="file-remove-choice">
        <Button onClick={run(onArchive)} disabled={busy}>{labels.archiveAction}</Button>
        <div className="muted">{labels.archiveHint}</div>
      </div>

      <div className="file-remove-choice">
        {/* Red: the irreversible option must not look like the reversible one above it. */}
        <Button
          onClick={run(onDelete)}
          disabled={busy}
          variant="danger"
        >
          {labels.deleteAction}
        </Button>
        <div className="muted">{labels.deleteHint}</div>
      </div>

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
